export const focusSelector = 'a, input, select, textarea, button, object, [tabindex]';

export const FOCUSABLE_ELEMENT_SELECTORS =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]';

/** Copied from VHS StringHelpers */
/**
 * Coerces a value `value` into a string. If passed a null or undefined
 * value, returns an empty string.
 *
 * @param {*} value - a value of any type
 * @returns {string} - that value as a string
 */
export const coerceToString = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value); // eslint-disable-line @typescript-eslint/no-base-to-string

const isHiddenElement = (element: HTMLElement) => {
  const { display, visibility } = window.getComputedStyle(element);
  const isHidden =
    display === 'none' ||
    element.style.display === 'none' ||
    visibility === 'none' ||
    element.style.visibility === 'hidden';
  return (element.offsetWidth <= 0 && element.offsetHeight <= 0) || isHidden;
};

const isVisibleElement = (element: HTMLElement) => {
  let parentElement: HTMLElement | null = element;

  while (parentElement) {
    if (parentElement === document.body) {
      break;
    }
    if (isHiddenElement(parentElement)) {
      return false;
    }
    parentElement = parentElement.parentNode as HTMLElement | null;
  }

  return true;
};

const getElementTabIndex = (element: HTMLElement) => {
  const tabIndex = element.getAttribute('tabindex');

  // @ts-expect-error - The logic was written this way pre-TS conversion, and there are checks for NaN below which is the case if the index is undefined
  return Number.parseInt(tabIndex ?? undefined, 10);
};

const isTabIndexNaN = (element: HTMLElement) => {
  const tabIndex = getElementTabIndex(element);
  return Number.isNaN(tabIndex);
};

export const isFocusableElement = (element: HTMLElement) => {
  const tabbableNodeRegEx = /input|select|textarea|button|object/;
  const nodeName = element.nodeName.toLowerCase();
  const isTabIndexNotNaN = !isTabIndexNaN(element);
  const isFocusable =
    // @ts-expect-error - Disabled is specific to buttons and inputs, but we could be dealing with any number of types here.  Disabled would be undefined for those, so ignoring.
    (tabbableNodeRegEx.test(nodeName) && !(element.disabled as boolean)) ||
    (element instanceof HTMLAnchorElement ? element.href || isTabIndexNotNaN : isTabIndexNotNaN);

  return Boolean(isFocusable) && isVisibleElement(element);
};

// elements a user can tab between, excluding elements with tabIndex=-1
export const isTabbableElement = (element: HTMLElement) => {
  const tabIndex = getElementTabIndex(element);
  return (isTabIndexNaN(element) || tabIndex >= 0) && isFocusableElement(element);
};

export const findTabbableDescendants = (element: HTMLElement) =>
  Array.from<HTMLElement>(element.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS)).filter(
    isTabbableElement,
  );

const focusLaterElements: HTMLElement[] = [];

let focusElement: HTMLElement | null = null;
let needToFocus = false;

const handleBlur = () => {
  needToFocus = true;
};

const handleFocus = () => {
  if (needToFocus) {
    needToFocus = false;

    if (!focusElement) {
      return;
    }

    if (focusElement.contains(document.activeElement)) {
      return;
    }

    const element = findTabbableDescendants(focusElement)[0] ?? focusElement;
    element.focus();
  }
};

export const markForFocusLater = () => {
  const element = document.activeElement as HTMLElement | null;

  if (element !== null) {
    focusLaterElements.push(element);
  }
};

export const returnFocus = () => {
  let toFocus = null;

  try {
    toFocus = focusLaterElements.pop();
    if (toFocus) {
      toFocus.focus();
    }
  } catch {
    // eslint-disable-next-line no-console
    console.warn(
      `You tried to return focus to ${coerceToString(toFocus)} but it is not in the DOM anymore`,
    );
  }
};

export const setupScopedFocus = (element: HTMLElement) => {
  focusElement = element;
  document.addEventListener('focusout', handleBlur, false);
  document.addEventListener('focusin', handleFocus, true);
};

export const teardownScopedFocus = () => {
  focusElement = null;
  document.removeEventListener('focusout', handleBlur);
  document.removeEventListener('focusin', handleFocus);
};

// Controls tabbing, so it stays inside the node element
export const scopeTab = (node: HTMLElement, event: KeyboardEvent) => {
  const tabbable = findTabbableDescendants(node);

  if (!tabbable.length) {
    event.preventDefault();
    return;
  }

  const finalTabbable = tabbable[event.shiftKey ? 0 : tabbable.length - 1];

  // handle immediate shift+tab after opening with mouse
  const leavingFinalTabbable =
    finalTabbable === document.activeElement || node === document.activeElement;

  if (!leavingFinalTabbable) {
    return;
  }

  event.preventDefault();

  const target = tabbable[event.shiftKey ? tabbable.length - 1 : 0];

  if (target) {
    target.focus();
  }
};

// Sets `aria-hidden` on all elements on the body, except for the active node
export const createAriaHider = (containerNode: HTMLElement, selector?: string) => {
  selector ??= 'body > :not(script)'; // eslint-disable-line no-param-reassign

  const rootNodes = Array.from(document.querySelectorAll(selector)).map((node) => {
    if (node.contains(containerNode)) {
      return undefined;
    }

    const ariaHidden = node.getAttribute('aria-hidden');

    if (ariaHidden === null || ariaHidden === 'false') {
      node.setAttribute('aria-hidden', 'true');
    }

    return {
      node,
      ariaHidden,
    };
  });

  return () => {
    rootNodes.forEach((item) => {
      if (!item) {
        return;
      }

      if (item.ariaHidden === null) {
        item.node.removeAttribute('aria-hidden');
      } else {
        item.node.setAttribute('aria-hidden', item.ariaHidden);
      }
    });
  };
};
